import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { UserDetailsService } from '../user-details.service';
import { DfSideNavItem } from '@design-factory/design-factory';
import { NavigationMenuIdentifiers } from './navigation-menu-identifiers';
import {
  PERMISSION_MANAGE_EDIFACT_RULE,
  PERMISSION_MANAGE_FAMILY,
  PERMISSION_MANAGE_FARES,
  PERMISSION_MANAGE_HOTEL,
  PERMISSION_MANAGE_MARKET,
  PERMISSION_MANAGE_NDC_RULE,
  PERMISSION_MANAGE_POS,
  PERMISSION_VIEW_FAMILY,
  PERMISSION_VIEW_FARES,
  PERMISSION_VIEW_FLIGHT,
  PERMISSION_VIEW_HOTEL,
  PERMISSION_VIEW_MARKET,
  PERMISSION_VIEW_POS
} from '../user-permissions.service';
import { LookupOptions } from '../../core/util/lookup-options';
import {
  ClearLookupData,
  ResetPartitionDestination,
  SetNavigationParams,
  SetPartitionDestination
} from '../../store/navigation/navigation-actions';
import { UpdateSelectedPartition } from '../../store/core/actions';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  selectedMenuTitle = new BehaviorSubject('Dashboard');
  menuStateChanged$ = new Subject<void>();
  isPosCollapsed = true;
  isMarketsCollapsed = true;
  isHotelsCollapsed = true;
  isFlightsCollapsed = true;
  isFamiliesCollapsed = true;
  isFaresCollapsed = true;
  navigationDisabled = false;
  familiesManageEnabled = false;
  familiesViewEnabled = false;
  flightsManageEnabled = false;
  flightsViewEnabled = false;
  hotelsManageEnabled = false;
  hotelsViewEnabled = false;
  marketsManageEnabled = false;
  marketsViewEnabled = false;
  posManageEnabled = false;
  posViewEnabled = false;
  faresManageEnabled = false;
  faresViewEnabled = false;
  isHotelFamiliesCollapsed = true;
  isAirFamiliesCollapsed = true;

  constructor(
    private readonly userDetailsService: UserDetailsService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store
  ) {
    const permissions = this.userDetailsService.loggedInUser?.permissions;
    this.familiesManageEnabled = Boolean(
      permissions && permissions.filter((p) => p === PERMISSION_MANAGE_FAMILY).length > 0
    );
    this.familiesViewEnabled = Boolean(
      permissions && permissions.filter((p) => p === PERMISSION_VIEW_FAMILY).length > 0
    );
    this.flightsViewEnabled = Boolean(
      permissions && permissions.filter((p) => p === PERMISSION_VIEW_FLIGHT).length > 0
    );
    this.hotelsManageEnabled = Boolean(
      permissions && permissions.filter((p) => p === PERMISSION_MANAGE_HOTEL).length > 0
    );
    this.hotelsViewEnabled = Boolean(permissions && permissions.filter((p) => p === PERMISSION_VIEW_HOTEL).length > 0);
    this.marketsManageEnabled = Boolean(
      permissions && permissions.filter((p) => p === PERMISSION_MANAGE_MARKET).length > 0
    );
    this.marketsViewEnabled = Boolean(
      permissions && permissions.filter((p) => p === PERMISSION_VIEW_MARKET).length > 0
    );
    this.posManageEnabled = Boolean(permissions && permissions.filter((p) => p === PERMISSION_MANAGE_POS).length > 0);
    this.posViewEnabled = Boolean(permissions && permissions.filter((p) => p === PERMISSION_VIEW_POS).length > 0);
    this.faresManageEnabled = Boolean(
      permissions && permissions.filter((p) => p === PERMISSION_MANAGE_FARES).length > 0
    );
    this.faresViewEnabled = Boolean(permissions && permissions.filter((p) => p === PERMISSION_VIEW_FARES).length > 0);
    this.setFlightsPermissions();
  }

  setSelectedMenuTitle(title: string) {
    this.selectedMenuTitle.next(title);
  }

  togglePos() {
    this.isPosCollapsed = !this.isPosCollapsed;
  }

  activatePos() {
    this.isPosCollapsed = false;
    this.menuStateChanged$.next();
  }

  toggleMarkets() {
    this.isMarketsCollapsed = !this.isMarketsCollapsed;
  }

  activateMarkets() {
    this.isMarketsCollapsed = false;
    this.menuStateChanged$.next();
  }

  toggleHotels() {
    this.isHotelsCollapsed = !this.isHotelsCollapsed;
  }

  activateHotels() {
    this.isHotelsCollapsed = false;
    this.menuStateChanged$.next();
  }

  toggleFlights() {
    this.isFlightsCollapsed = !this.isFlightsCollapsed;
  }
  activateFlights() {
    this.isFlightsCollapsed = false;
    this.menuStateChanged$.next();
  }

  toggleFamilies() {
    this.isFamiliesCollapsed = !this.isFamiliesCollapsed;
    if (this.isFamiliesCollapsed) {
      this.isHotelFamiliesCollapsed = true;
      this.isAirFamiliesCollapsed = true;
    }
  }
  activateFamilies() {
    this.isFamiliesCollapsed = false;
    this.menuStateChanged$.next();
  }

  activateHotelFamilies() {
    this.isFamiliesCollapsed = false;
    this.isHotelFamiliesCollapsed = false;
    this.menuStateChanged$.next();
  }

  activateAirFamilies() {
    this.isFamiliesCollapsed = false;
    this.isAirFamiliesCollapsed = false;
    this.menuStateChanged$.next();
  }

  toggleFares() {
    this.isFaresCollapsed = !this.isFaresCollapsed;
  }

  activateFares() {
    this.isFaresCollapsed = false;
    this.menuStateChanged$.next();
  }

  enableNavigation() {
    this.navigationDisabled = false;
  }

  disableNavigation() {
    this.navigationDisabled = true;
  }

  isNavigationDisabled(): boolean {
    return this.navigationDisabled;
  }

  navigate(lookupOptions?: LookupOptions): void {
    this.store.dispatch(new SetNavigationParams(lookupOptions));

    this.router.navigate([lookupOptions?.destinationComponent]);
  }

  clearLookupData(): void {
    this.store.dispatch(new ClearLookupData());
  }

  selectPartition(partition: string): void {
    this.store.dispatch(new SetPartitionDestination(partition));
    if (this.router.url === '/dashboard') {
      this.onPartitionUpdateConfirmation(partition);
    } else {
      this.router.navigate(['dashboard']).then((result) => {
        if (result) {
          this.onPartitionUpdateConfirmation(partition);
        } else {
          this.store.dispatch(new ResetPartitionDestination());
        }
      });
    }
  }

  setFlightsPermissions(): void {
    const permissions = this.userDetailsService.loggedInUser?.permissions ?? [];
    this.flightsManageEnabled =
      permissions.filter((p) => p === PERMISSION_MANAGE_EDIFACT_RULE || p === PERMISSION_MANAGE_NDC_RULE).length > 0;
  }

  syncFromDfSideNavItems(items: DfSideNavItem[] | null): void {
    if (!items || items.length === 0) {
      return;
    }

    // Update collapsed states based on DF sidenav item states
    items.forEach((item) => {
      if (item.value && item.isCollapsed !== undefined) {
        switch (item.value) {
          case NavigationMenuIdentifiers.POS:
            this.isPosCollapsed = item.isCollapsed;
            break;
          case NavigationMenuIdentifiers.MARKETS:
            this.isMarketsCollapsed = item.isCollapsed;
            break;
          case NavigationMenuIdentifiers.FAMILIES:
            this.isFamiliesCollapsed = item.isCollapsed;
            // Also sync nested family submenus
            if (item.subMenus) {
              item.subMenus.forEach((subItem) => {
                if (subItem.value === NavigationMenuIdentifiers.HOTEL_FAMILIES && subItem.isCollapsed !== undefined) {
                  this.isHotelFamiliesCollapsed = subItem.isCollapsed;
                } else if (
                  subItem.value === NavigationMenuIdentifiers.AIR_FAMILIES &&
                  subItem.isCollapsed !== undefined
                ) {
                  this.isAirFamiliesCollapsed = subItem.isCollapsed;
                }
              });
            }
            break;
          case NavigationMenuIdentifiers.FARES:
            this.isFaresCollapsed = item.isCollapsed;
            break;
          case NavigationMenuIdentifiers.FLIGHTS:
            this.isFlightsCollapsed = item.isCollapsed;
            break;
          case NavigationMenuIdentifiers.HOTELS:
            this.isHotelsCollapsed = item.isCollapsed;
            break;
        }
      }
    });
  }

  private onPartitionUpdateConfirmation(partition: string): void {
    this.store.dispatch(new UpdateSelectedPartition(partition));
    this.collapseAllMenus();
  }

  private collapseAllMenus(): void {
    this.isPosCollapsed = true;
    this.isFamiliesCollapsed = true;
    this.isFlightsCollapsed = true;
    this.isHotelsCollapsed = true;
    this.isMarketsCollapsed = true;
    this.isAirFamiliesCollapsed = true;
    this.isHotelFamiliesCollapsed = true;
  }
}
